import React from "react";

import { Grid } from "@mui/material";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { BiMicrophone, BiMicrophoneOff } from "react-icons/bi";
import { FiLink, FiVideoOff, FiVideo } from "react-icons/fi";

import Button, { Color } from "../../../Form/Button";
import Switch from "../../../Form/Switch";
import SkeletonComponent from "../../../Generic/Skeleton";
import useLocalAudioToggle from "../../hooks/useLocalAudioToggle/useLocalAudioToggle";
import useLocalVideoToggle from "../../hooks/useLocalVideoToggle/useLocalVideoToggle";
import useVideoContext from "../../hooks/useVideoContext/useVideoContext";
import VideoInputList from "../../VideoInputList/VideoInputList";
import useStyles from "./styles";

interface Props {
  openTests: () => void;
  openShareLink: () => void;
  disableButtons: boolean;
  loading?: boolean;
}

const JoinModal = (props: Props) => {
  const { disableButtons, openShareLink, openTests, loading } = props;

  const classes = useStyles();
  const { t: translate } = useTranslation();

  const { task, videoSurvey } = useVideoContext();

  const [isAudioEnabled, toggleAudioEnabled] = useLocalAudioToggle();
  const [isVideoEnabled, toggleVideoEnabled] = useLocalVideoToggle();

  return (
    <div className="w-100">
      <SkeletonComponent isLoading={loading || false}>
        <section className={classes.section}>
          <header>
            <Grid container direction="row" alignItems="center" justifyContent="space-between">
              <Grid item md={6} lg={6}>
                <div id="customer-name">
                  <p data-cy="customerName" className={classes.customerName}>
                    {task?.job.customer?.name}
                  </p>
                  {task?.startDate && (
                    <span className={classes.taskStartDate}>
                      {translate("bookedFor")}&#160;
                      {moment(task?.startDate).format("DD/MM/YYYY")}&#160;
                      {translate("at")}&#160;
                      {moment(task?.startDate).format("hh:mma")}
                    </span>
                  )}
                </div>
              </Grid>
              <Grid
                item
                container
                md={6}
                lg={6}
                alignItems="flex-end"
                justifyContent="flex-end"
                spacing={2}
              >
                <Grid item>
                  <Button
                    data-cy="settingsButton"
                    disabled={
                      !videoSurvey?.iceServers ||
                      !videoSurvey?.token ||
                      !videoSurvey?.voiceToken ||
                      disableButtons
                    }
                    className={classes.buttonSettings}
                    customColor={Color.INFO}
                    onClick={openTests}
                    loading={
                      !videoSurvey?.iceServers || !videoSurvey?.token || !videoSurvey?.voiceToken
                    }
                  >
                    {translate("settings")}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    data-cy="shareLinkButton"
                    className={classes.buttonsShareLink}
                    customColor={Color.SECONDARY}
                    onClick={openShareLink}
                  >
                    <FiLink size={16} className="mr-2" />
                    {translate("shareLink")}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </header>
        </section>
      </SkeletonComponent>
      <section className={classes.videoSection}>
        <SkeletonComponent isLoading={loading || false}>
          {!isVideoEnabled ? (
            <div className={classes.noCameraAvailable}>
              <FiVideoOff size={44} color="#475467" className="mb-2" />
              <span className="cameraOff">{translate("cameraOff")}</span>
            </div>
          ) : (
            <div>
              <VideoInputList />
            </div>
          )}
        </SkeletonComponent>
        <SkeletonComponent isLoading={loading || false}>
          <div className={classes.options}>
            <div id="video">
              <Switch
                label={
                  <div className={classes.option}>
                    {isVideoEnabled ? (
                      <FiVideo size={16} color="#475467" className="mr-2" />
                    ) : (
                      <FiVideoOff size={16} color="#475467" className="mr-2" />
                    )}
                    <span>{translate("video")}</span>
                  </div>
                }
                labelPlacement="start"
                checked={isVideoEnabled || false}
                onChange={(_, checked) => toggleVideoEnabled()}
              />
            </div>
            <div id="microphone">
              <Switch
                label={
                  <div className={classes.option}>
                    {isAudioEnabled ? (
                      <BiMicrophone size={16} color="#475467" className="mr-2" />
                    ) : (
                      <BiMicrophoneOff size={16} color="#475467" className="mr-2" />
                    )}
                    <span>{translate("microphone")}</span>
                  </div>
                }
                labelPlacement="start"
                checked={isAudioEnabled || false}
                onChange={(_, checked) => toggleAudioEnabled()}
              />
            </div>
          </div>
        </SkeletonComponent>
      </section>
    </div>
  );
};

export default JoinModal;
